import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type ChipColor = 'gray' | 'red' | 'amber' | 'green' | 'blue' | 'purple';

/**
 * Small rounded status pill used across dashboard widgets: a soft tinted background +
 * solid colored text, fully rounded.
 *
 * The palette is applied via inline `style` on purpose: the app's Tailwind theme exposes
 * only semantic tokens (muted / destructive / primary …), not the full color scale, so
 * raw `bg-red-500/10` utilities render as a near-invisible wash here (see the note in
 * `components/ui/badge.tsx`). The text color is identical across light/dark and only the
 * background alpha changes, so these fixed values read correctly in both themes.
 */
const CHIP_PALETTE: Record<ChipColor, { fg: string; bg: string }> = {
  gray: { fg: '#6b7280', bg: 'rgba(107, 114, 128, 0.14)' },
  red: { fg: '#ef4444', bg: 'rgba(239, 68, 68, 0.14)' },
  amber: { fg: '#f59e0b', bg: 'rgba(245, 158, 11, 0.14)' },
  green: { fg: '#10b981', bg: 'rgba(16, 185, 129, 0.14)' },
  blue: { fg: '#3b82f6', bg: 'rgba(59, 130, 246, 0.14)' },
  purple: { fg: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.14)' },
};

export function Chip({
  color = 'gray',
  className,
  children,
}: {
  color?: ChipColor;
  className?: string;
  children: ReactNode;
}) {
  const { fg, bg } = CHIP_PALETTE[color];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        className,
      )}
      style={{ backgroundColor: bg, color: fg }}
    >
      {children}
    </span>
  );
}
